import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import { useToast } from '../components/Toast';
import { PromoteDialog } from './PromoteDialog';

interface Props {
  projectId: string;
  onBack: () => void;
}

const PHASE_ORDER = ['planning', 'research', 'content', 'media', 'render', 'commit', 'snapshot', 'validate'];

export function ProjectDetailScreen({ projectId, onBack }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const { data: state, isLoading } = useQuery({
    queryKey: ['projectState', projectId],
    queryFn: () => api.getProjectState(projectId),
    // Poll faster while a build is running (2s), slower otherwise (10s).
    refetchInterval: (q) => {
      const s = q.state.data?.buildRun?.status;
      return s === 'running' || s === 'queued' ? 2000 : 10000;
    },
  });

  const { data: validation } = useQuery({
    queryKey: ['validation', projectId],
    queryFn: () => api.latestValidation(projectId),
    enabled: state?.buildRun?.status === 'completed',
    refetchInterval: false,
  });

  if (isLoading) return <div className="text-gray-500">Loading…</div>;
  if (!state) return <div>Project not found</div>;

  const br = state.buildRun;
  const phaseIdx = br?.lastCompletedPhase ? PHASE_ORDER.indexOf(br.lastCompletedPhase) : -1;

  return (
    <div>
      <button onClick={onBack} className="text-sm text-gray-600 hover:text-gray-900 mb-4">← All projects</button>

      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h1 className="text-2xl font-semibold mb-1">
          {state.project.displayName || state.project.brandId}
        </h1>
        <div className="text-sm text-gray-500 space-x-3">
          <span>Kit: {state.project.kitId}</span>
          <span>·</span>
          <span>Created: {new Date(state.project.createdAt).toLocaleString()}</span>
        </div>
      </div>

      {/* Module F-Lite — review + accept + connect-domain CTAs */}
      <AcceptanceAndPromoteSection
        state={state}
        accepting={accepting}
        onAccept={async () => {
          setAccepting(true);
          try {
            await api.acceptProject(projectId);
            toast.success('Site accepted — you can now connect a custom domain');
            qc.invalidateQueries({ queryKey: ['projectState', projectId] });
          } catch (err) {
            const msg = err instanceof ApiError ? err.message : String(err);
            toast.error(msg);
          } finally {
            setAccepting(false);
          }
        }}
        onPromote={() => setPromoteOpen(true)}
      />
      {promoteOpen && (
        <PromoteDialog
          projectId={projectId}
          onClose={() => setPromoteOpen(false)}
          onPromoted={() => qc.invalidateQueries({ queryKey: ['projectState', projectId] })}
        />
      )}

      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h2 className="font-semibold mb-3">Current BuildRun</h2>
        {!br ? (
          <div className="text-gray-500">No BuildRun yet.</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">Status</div>
                <div className="font-medium">
                  <StatusBadge status={br.status} />
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">Cost (USD)</div>
                <div className="font-medium">${br.totalCostUsd?.toFixed(3) ?? '—'}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">Started</div>
                <div className="font-medium">{new Date(br.startedAt).toLocaleString()}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider mb-0.5">Completed</div>
                <div className="font-medium">{br.completedAt ? new Date(br.completedAt).toLocaleString() : '—'}</div>
              </div>
            </div>

            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">Pipeline</div>
              <div className="flex flex-wrap gap-2">
                {PHASE_ORDER.map((phase, idx) => {
                  const done = idx <= phaseIdx;
                  const active = idx === phaseIdx + 1 && br.status === 'running';
                  return (
                    <div
                      key={phase}
                      className={
                        'px-3 py-1 rounded text-xs font-medium ' +
                        (done
                          ? 'bg-green-100 text-green-800'
                          : active
                          ? 'bg-blue-100 text-blue-800 animate-pulse'
                          : 'bg-gray-100 text-gray-500')
                      }
                    >
                      {phase}
                    </div>
                  );
                })}
              </div>
            </div>

            {br.snapshotId && (
              <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-100">
                <div className="text-xs text-gray-500 font-mono truncate">snapshot: {br.snapshotId}</div>
                {br.status === 'completed' && (
                  <DownloadSnapshotButton projectId={projectId} snapshotId={br.snapshotId} />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {validation && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h2 className="font-semibold mb-3">Validation</h2>
          <div className="flex items-baseline gap-4 text-sm mb-3">
            <span className={validation.report.outcome === 'pass' ? 'text-green-700 font-semibold' : 'text-red-700 font-semibold'}>
              {validation.report.outcome.toUpperCase()}
            </span>
            <span className="text-gray-600">{Object.keys(validation.report.data.pages).length} pages</span>
          </div>
          {Object.entries(validation.report.data.pages).map(([slug, page]) => {
            const blocking = page.issues.filter((i) => i.severity === 'blocking');
            if (blocking.length === 0) return null;
            return (
              <details key={slug} className="text-sm mb-2">
                <summary className="cursor-pointer text-red-700">
                  {slug} — {blocking.length} blocking
                </summary>
                <ul className="ml-4 mt-1 list-disc text-gray-700 text-xs">
                  {blocking.map((i, idx) => (
                    <li key={idx}><code className="bg-gray-100 px-1">{i.ruleId}</code> — {i.message}</li>
                  ))}
                </ul>
              </details>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DownloadSnapshotButton({ projectId, snapshotId }: { projectId: string; snapshotId: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.downloadSnapshot(projectId, snapshotId);
    } catch (err) {
      setError(String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-end">
      <button
        type="button"
        onClick={handle}
        disabled={busy}
        className="text-xs bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-800 px-3 py-1.5 rounded font-medium"
        title="Hand this .tar.gz to the client — they can host it anywhere"
      >
        {busy ? 'Preparing…' : '↓ Download site (tar.gz)'}
      </button>
      {error && <span className="text-xs text-red-600 mt-1">{error}</span>}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    queued: 'bg-yellow-100 text-yellow-800',
    running: 'bg-blue-100 text-blue-800',
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${colors[status] ?? 'bg-gray-100'}`}>
      {status}
    </span>
  );
}

interface AcceptanceProps {
  state: { project: { id: string; brandId: string; acceptedAt?: string; autoAccept: boolean; liveDeploymentId?: string }; buildRun?: { status: string } };
  accepting: boolean;
  onAccept: () => void;
  onPromote: () => void;
}

function AcceptanceAndPromoteSection({ state, accepting, onAccept, onPromote }: AcceptanceProps) {
  const { project, buildRun } = state;
  const buildIsDone = buildRun?.status === 'completed';
  const accepted = project.acceptedAt != null || project.autoAccept === true;
  // Best-effort staging URL — siteskick subdomain pattern.
  const stagingUrl = `https://${project.brandId}.sites.siteskick.com/`;

  // While the build is still running (or hasn't started), don't show any
  // acceptance/promote UI — would confuse the user.
  if (!buildIsDone) return null;

  // Build done, not accepted yet — show review CTA.
  if (!accepted) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-6">
        <div className="flex items-start gap-3">
          <div className="text-2xl leading-none">👀</div>
          <div className="flex-1">
            <h2 className="font-semibold text-amber-900 mb-1">Review required</h2>
            <p className="text-sm text-amber-800 mb-3">
              Your site has finished building. Open it in a new tab, review the content
              + images, and accept it when you're happy.
            </p>
            <div className="flex flex-wrap gap-2">
              <a
                href={stagingUrl}
                target="_blank"
                rel="noreferrer"
                className="bg-white text-amber-900 border border-amber-300 hover:bg-amber-50 font-medium px-4 py-2 rounded text-sm"
              >
                ↗ Open staging site
              </a>
              <button
                type="button"
                onClick={onAccept}
                disabled={accepting}
                className="bg-amber-600 text-white font-medium px-4 py-2 rounded text-sm hover:bg-amber-700 disabled:opacity-50"
              >
                {accepting ? 'Accepting…' : '✓ Accept site'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Accepted but not live on a custom domain yet → show promote CTA.
  if (!project.liveDeploymentId) {
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-5 mb-6">
        <div className="flex items-start gap-3">
          <div className="text-2xl leading-none">🌐</div>
          <div className="flex-1">
            <h2 className="font-semibold text-blue-900 mb-1">
              Ready to connect a custom domain
              {project.autoAccept && project.acceptedAt == null ? ' (auto-accepted)' : ''}
            </h2>
            <p className="text-sm text-blue-800 mb-3">
              Site accepted. Buy a domain, add it to the CF Pages project in the CF
              dashboard, then click below to flip this site to production.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={onPromote}
                className="bg-blue-600 text-white font-medium px-4 py-2 rounded text-sm hover:bg-blue-700"
              >
                Connect custom domain
              </button>
              <a
                href={stagingUrl}
                target="_blank"
                rel="noreferrer"
                className="text-sm text-blue-800 underline px-2 py-2"
              >
                Open staging site
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Already live on a custom domain.
  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-5 mb-6">
      <div className="flex items-start gap-3">
        <div className="text-2xl leading-none">✓</div>
        <div className="flex-1">
          <h2 className="font-semibold text-green-900 mb-1">Live on a custom domain</h2>
          <p className="text-sm text-green-800">
            Deployment id <code className="bg-white px-1 rounded font-mono text-xs">{project.liveDeploymentId}</code>.
            Operate via the existing Deployments + Validation surfaces below.
          </p>
        </div>
      </div>
    </div>
  );
}
